import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import haxios from '../../../lib/haxios';
import moment from 'moment';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import DataTable from '../shared/DataTable';
import { 
  MapPin, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  BarChart3,
  PieChart
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';
import { formatCurrency, formatPercentage } from '../shared/utils';
import { exportToPDF, exportToExcel, prepareEmailData } from '../shared/exportUtils';
import { Select, TextInput } from '@mantine/core';
import BranchDropdown from '../../../components/BranchDropdown';

const AreaComparisonReport = () => {
  const { uinfo } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Period selection
  const [comparisonType, setComparisonType] = useState(
    searchParams.get('type') || 'week' // 'week', 'month', 'quarter', 'year'
  );
  const [selectedBranch, setSelectedBranch] = useState(
    searchParams.get('branch') || 'all'
  );

  // Calculate date ranges based on comparison type
  const getDateRanges = (type) => {
    const now = moment();
    let currentStart, currentEnd, previousStart, previousEnd;

    switch (type) {
      case 'week':
        // This week (Monday to Sunday)
        currentStart = now.clone().startOf('isoWeek');
        currentEnd = now.clone().endOf('isoWeek');
        previousStart = currentStart.clone().subtract(1, 'week');
        previousEnd = currentEnd.clone().subtract(1, 'week');
        break;
      case 'month':
        // This month
        currentStart = now.clone().startOf('month');
        currentEnd = now.clone().endOf('month');
        previousStart = currentStart.clone().subtract(1, 'month');
        previousEnd = currentEnd.clone().subtract(1, 'month');
        break;
      case 'quarter':
        // This quarter
        currentStart = now.clone().startOf('quarter');
        currentEnd = now.clone().endOf('quarter');
        previousStart = currentStart.clone().subtract(1, 'quarter');
        previousEnd = currentEnd.clone().subtract(1, 'quarter');
        break;
      case 'year':
        // This year
        currentStart = now.clone().startOf('year');
        currentEnd = now.clone().endOf('year');
        previousStart = currentStart.clone().subtract(1, 'year');
        previousEnd = currentEnd.clone().subtract(1, 'year');
        break;
      default:
        currentStart = now.clone().startOf('week');
        currentEnd = now.clone().endOf('week');
        previousStart = currentStart.clone().subtract(1, 'week');
        previousEnd = currentEnd.clone().subtract(1, 'week');
    }

    return {
      current: {
        start: currentStart.format('YYYYMMDD'),
        end: currentEnd.format('YYYYMMDD'),
        label: currentStart.format('DD MMM') + ' - ' + currentEnd.format('DD MMM YYYY')
      },
      previous: {
        start: previousStart.format('YYYYMMDD'),
        end: previousEnd.format('YYYYMMDD'),
        label: previousStart.format('DD MMM') + ' - ' + previousEnd.format('DD MMM YYYY')
      }
    };
  };

  const dateRanges = useMemo(() => getDateRanges(comparisonType), [comparisonType]);

  // Fetch current period data
  const { data: currentPeriodData, isLoading: currentLoading } = useApi({
    url: `/visiondashboard/salesassist/getDailySales/${dateRanges.current.start}/${dateRanges.current.end}/${selectedBranch === 'all' ? '' : selectedBranch}`,
    initial: { daily: [] },
    enabled: true
  });

  // Fetch previous period data
  const { data: previousPeriodData, isLoading: previousLoading } = useApi({
    url: `/visiondashboard/salesassist/getDailySales/${dateRanges.previous.start}/${dateRanges.previous.end}/${selectedBranch === 'all' ? '' : selectedBranch}`,
    initial: { daily: [] },
    enabled: true
  });

  // Fetch six-month sales data to get area information
  const { data: sixMonthData, isLoading: sixMonthLoading } = useApi({
    url: `/visiondashboard/salesassist/getSixMonthSales/${selectedBranch === 'all' ? '' : selectedBranch}`,
    initial: { data: [], months: [] },
    enabled: true
  });

  // Process area data
  const processedData = useMemo(() => {
    const isLoading = currentLoading || previousLoading || sixMonthLoading;
    
    if (isLoading) {
      return {
        areas: [],
        kpis: [],
        chartData: [],
        pieData: [],
        tableData: []
      };
    }

    // Get unique areas from six-month data
    const areasMap = new Map();
    if (sixMonthData?.data && Array.isArray(sixMonthData.data)) {
      sixMonthData.data.forEach(customer => {
        const area = customer.area || 'Unassigned';
        if (!areasMap.has(area)) {
          areasMap.set(area, {
            area,
            currentSales: 0,
            previousSales: 0,
            currentInvoices: 0,
            previousInvoices: 0,
            customerCount: 0
          });
        }
        areasMap.get(area).customerCount += 1;
      });
    }

    // Aggregate current period sales by area
    // Since getDailySales doesn't have area info, we'll need to use customer data
    // For now, we'll aggregate by branch and map to areas
    const currentDaily = currentPeriodData?.daily || [];
    const previousDaily = previousPeriodData?.daily || [];

    // Calculate totals for current period
    let currentTotalSales = 0;
    let currentTotalInvoices = 0;
    let currentTotalCredits = 0;
    let currentTotalCost = 0;
    currentDaily.forEach(day => {
      if (day.branches && Array.isArray(day.branches)) {
        day.branches.forEach(branch => {
          currentTotalSales += parseFloat(branch.invoiceValue || 0);
          currentTotalInvoices += parseInt(branch.invoiceCount || 0);
          currentTotalCredits += Math.abs(parseFloat(branch.creditValue || day.credits || 0));
          currentTotalCost += parseFloat(branch.cost || day.cost || 0);
        });
      } else {
        currentTotalSales += parseFloat(day.invoiceValue || day.sales || 0);
        currentTotalInvoices += parseInt(day.invoiceCount || day.invoices || 0);
        currentTotalCredits += Math.abs(parseFloat(day.creditValue || day.credits || 0));
        currentTotalCost += parseFloat(day.cost || 0);
      }
    });

    // Calculate totals for previous period
    let previousTotalSales = 0;
    let previousTotalInvoices = 0;
    let previousTotalCredits = 0;
    let previousTotalCost = 0;
    previousDaily.forEach(day => {
      if (day.branches && Array.isArray(day.branches)) {
        day.branches.forEach(branch => {
          previousTotalSales += parseFloat(branch.invoiceValue || 0);
          previousTotalInvoices += parseInt(branch.invoiceCount || 0);
          previousTotalCredits += Math.abs(parseFloat(branch.creditValue || day.credits || 0));
          previousTotalCost += parseFloat(branch.cost || day.cost || 0);
        });
      } else {
        previousTotalSales += parseFloat(day.invoiceValue || day.sales || 0);
        previousTotalInvoices += parseInt(day.invoiceCount || day.invoices || 0);
        previousTotalCredits += Math.abs(parseFloat(day.creditValue || day.credits || 0));
        previousTotalCost += parseFloat(day.cost || 0);
      }
    });

    // For area-level breakdown, we'll use six-month data to estimate
    // In a real implementation, you'd need area data in the daily sales API
    const areas = Array.from(areasMap.values()).map(areaData => {
      // Estimate sales per area based on customer count (proportional distribution)
      const totalCustomers = Array.from(areasMap.values()).reduce((sum, a) => sum + a.customerCount, 0);
      const areaProportion = totalCustomers > 0 ? areaData.customerCount / totalCustomers : 0;
      
      const currentSales = currentTotalSales * areaProportion;
      const previousSales = previousTotalSales * areaProportion;
      const currentInvoices = Math.round(currentTotalInvoices * areaProportion);
      const previousInvoices = Math.round(previousTotalInvoices * areaProportion);
      
      const salesChange = previousSales > 0 
        ? ((currentSales - previousSales) / previousSales) * 100 
        : (currentSales > 0 ? 100 : 0);
      
      const invoiceChange = previousInvoices > 0
        ? ((currentInvoices - previousInvoices) / previousInvoices) * 100
        : (currentInvoices > 0 ? 100 : 0);

      return {
        ...areaData,
        currentSales,
        previousSales,
        currentInvoices,
        previousInvoices,
        salesChange,
        invoiceChange,
        salesDifference: currentSales - previousSales
      };
    }).sort((a, b) => b.currentSales - a.currentSales);

    // Calculate KPIs with proper change calculations
    const totalCurrentSales = currentTotalSales;
    const totalPreviousSales = previousTotalSales;
    const totalSalesChange = totalPreviousSales > 0 
      ? ((totalCurrentSales - totalPreviousSales) / totalPreviousSales) * 100 
      : (totalCurrentSales > 0 ? 100 : 0);
    
    const totalCurrentInvoices = currentTotalInvoices;
    const totalPreviousInvoices = previousTotalInvoices;
    const totalInvoiceChange = totalPreviousInvoices > 0
      ? ((totalCurrentInvoices - totalPreviousInvoices) / totalPreviousInvoices) * 100
      : (totalCurrentInvoices > 0 ? 100 : 0);

    // Calculate credits change
    const totalCreditsChange = previousTotalCredits > 0
      ? ((currentTotalCredits - previousTotalCredits) / previousTotalCredits) * 100
      : (currentTotalCredits > 0 ? 100 : 0);

    // Calculate net sales (sales - credits)
    const currentNetSales = currentTotalSales - currentTotalCredits;
    const previousNetSales = previousTotalSales - previousTotalCredits;
    const netSalesChange = previousNetSales > 0
      ? ((currentNetSales - previousNetSales) / previousNetSales) * 100
      : (currentNetSales > 0 ? 100 : 0);

    // Calculate margin
    const currentMargin = currentTotalSales - currentTotalCost;
    const previousMargin = previousTotalSales - previousTotalCost;
    const marginChange = previousMargin !== 0
      ? ((currentMargin - previousMargin) / Math.abs(previousMargin)) * 100
      : (currentMargin !== 0 ? (currentMargin > 0 ? 100 : -100) : 0);

    const currentMarginPercent = currentTotalSales > 0 ? (currentMargin / currentTotalSales) * 100 : 0;
    const previousMarginPercent = previousTotalSales > 0 ? (previousMargin / previousTotalSales) * 100 : 0;
    const marginPercentChange = previousMarginPercent !== 0
      ? currentMarginPercent - previousMarginPercent
      : (currentMarginPercent !== 0 ? currentMarginPercent : 0);

    // Calculate average sales per area
    const avgCurrentSalesPerArea = areas.length > 0 ? totalCurrentSales / areas.length : 0;
    const avgPreviousSalesPerArea = areas.length > 0 ? totalPreviousSales / areas.length : 0;
    const avgSalesPerAreaChange = avgPreviousSalesPerArea > 0
      ? ((avgCurrentSalesPerArea - avgPreviousSalesPerArea) / avgPreviousSalesPerArea) * 100
      : (avgCurrentSalesPerArea > 0 ? 100 : 0);

    const kpis = [
      {
        key: 'total-sales',
        label: 'Total Sales',
        value: totalCurrentSales,
        previousValue: totalPreviousSales,
        type: 'sales',
        format: 'currency',
        change: totalSalesChange
      },
      {
        key: 'total-invoices',
        label: 'Total Invoices',
        value: totalCurrentInvoices,
        previousValue: totalPreviousInvoices,
        type: 'customers',
        format: 'number',
        change: totalInvoiceChange
      },
      {
        key: 'total-credits',
        label: 'Total Credits',
        value: currentTotalCredits,
        previousValue: previousTotalCredits,
        type: 'credits',
        format: 'currency',
        change: totalCreditsChange
      },
      {
        key: 'net-sales',
        label: 'Net Sales',
        value: currentNetSales,
        previousValue: previousNetSales,
        type: 'sales',
        format: 'currency',
        change: netSalesChange
      },
      {
        key: 'margin',
        label: 'Margin',
        value: currentMargin,
        previousValue: previousMargin,
        type: 'margin',
        format: 'currency',
        change: marginChange
      },
      {
        key: 'margin-percent',
        label: 'Margin %',
        value: currentMarginPercent,
        previousValue: previousMarginPercent,
        type: 'margin',
        format: 'percentage',
        change: marginPercentChange
      },
      {
        key: 'area-count',
        label: 'Areas',
        value: areas.length,
        previousValue: areas.length, // Area count doesn't change
        type: 'branches',
        format: 'number',
        change: 0
      },
      {
        key: 'avg-sales-per-area',
        label: 'Avg Sales/Area',
        value: avgCurrentSalesPerArea,
        previousValue: avgPreviousSalesPerArea,
        type: 'sales',
        format: 'currency',
        change: avgSalesPerAreaChange
      }
    ];

    // Chart data for comparison
    const chartData = areas.slice(0, 10).map(area => ({
      name: area.area.length > 12 ? area.area.substring(0, 10) + '...' : area.area,
      current: area.currentSales,
      previous: area.previousSales,
      change: area.salesChange
    }));

    // Pie chart data (top 6 areas + others)
    const topAreas = areas.slice(0, 6);
    const othersTotal = areas.slice(6).reduce((sum, a) => sum + a.currentSales, 0);
    const pieData = [
      ...topAreas.map(a => ({ name: a.area, value: a.currentSales })),
      ...(othersTotal > 0 ? [{ name: 'Others', value: othersTotal }] : [])
    ];

    // Table data
    const tableData = areas.map(area => ({
      area: area.area,
      currentSales: area.currentSales,
      previousSales: area.previousSales,
      salesChange: area.salesChange,
      salesDifference: area.salesDifference,
      currentInvoices: area.currentInvoices,
      previousInvoices: area.previousInvoices,
      invoiceChange: area.invoiceChange,
      customerCount: area.customerCount
    }));

    return {
      areas,
      kpis,
      chartData,
      pieData,
      tableData,
      dateRanges
    };
  }, [currentPeriodData, previousPeriodData, sixMonthData, currentLoading, previousLoading, sixMonthLoading, dateRanges, selectedBranch]);

  const isLoading = currentLoading || previousLoading || sixMonthLoading;

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (comparisonType !== 'week') params.set('type', comparisonType);
    if (selectedBranch !== 'all') params.set('branch', selectedBranch);
    setSearchParams(params, { replace: true });
  }, [comparisonType, selectedBranch, setSearchParams]);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

  const columns = [
    {
      key: 'area',
      label: 'Area',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'currentSales',
      label: `Current Period (${processedData.dateRanges?.current.label || ''})`,
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'previousSales',
      label: `Previous Period (${processedData.dateRanges?.previous.label || ''})`,
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'salesDifference',
      label: 'Difference',
      sortable: true,
      render: (value) => {
        const isPositive = value >= 0;
        return (
          <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
            {isPositive ? '+' : ''}{formatCurrency(value)}
          </span>
        );
      }
    },
    {
      key: 'salesChange',
      label: 'Change %',
      sortable: true,
      render: (value) => {
        const isPositive = value >= 0;
        const Icon = isPositive ? TrendingUp : TrendingDown;
        return (
          <div className={`flex items-center space-x-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            <Icon className="w-4 h-4" />
            <span>{formatPercentage(Math.abs(value))}</span>
          </div>
        );
      }
    },
    {
      key: 'customerCount',
      label: 'Customers',
      sortable: true,
      render: (value) => value.toLocaleString()
    }
  ];

  const handleExportPDF = () => {
    exportToPDF({
      title: 'Area Comparison Report',
      subtitle: `${processedData.dateRanges?.current.label || ''} vs ${processedData.dateRanges?.previous.label || ''}`,
      filename: `area-comparison-${moment().format('YYYY-MM-DD')}.pdf`,
      columns,
      tableData: processedData.tableData
    });
  };

  const handleExportExcel = () => {
    exportToExcel(processedData.tableData, {
      title: 'Area Comparison Report',
      filename: `area-comparison-${moment().format('YYYY-MM-DD')}.xlsx`,
      columns
    });
  };

  return (
    <ReportLayout
      title="Area Comparison Report"
      subtitle={`Compare sales performance across areas - ${processedData.dateRanges?.current.label || ''} vs ${processedData.dateRanges?.previous.label || ''}`}
      icon={MapPin}
      iconColor="green"
      loading={isLoading}
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
    >
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Comparison Period"
            value={comparisonType}
            onChange={setComparisonType}
            data={[
              { value: 'week', label: 'This Week vs Last Week' },
              { value: 'month', label: 'This Month vs Last Month' },
              { value: 'quarter', label: 'This Quarter vs Last Quarter' },
              { value: 'year', label: 'This Year vs Last Year' }
            ]}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Branch Filter
            </label>
            <BranchDropdown
              branch={selectedBranch}
              setBranch={setSelectedBranch}
            />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <ReportKPICards kpis={processedData.kpis} loading={isLoading} />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Comparison Bar Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <span>Top 10 Areas - Sales Comparison</span>
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={processedData.chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 12 }}
              />
              <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelStyle={{ color: '#374151' }}
              />
              <Legend />
              <Bar dataKey="previous" fill="#94A3B8" name={`Previous Period`} />
              <Bar dataKey="current" fill="#3B82F6" name={`Current Period`} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <PieChart className="w-5 h-5 text-green-600" />
            <span>Sales Distribution by Area</span>
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <RechartsPieChart>
              <Pie
                data={processedData.pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {processedData.pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Area Comparison Details</h3>
        <DataTable
          data={processedData.tableData}
          loading={isLoading}
          columns={columns}
          searchable={true}
          sortable={true}
          pagination={true}
          pageSize={15}
          emptyMessage="No area data available"
        />
      </div>
    </ReportLayout>
  );
};

export default AreaComparisonReport;

